import { createContext, type ReactNode, useContext } from 'react';
import type { CountrySelector, CountrySelectorList } from './types';
import { useRouter } from 'next/router';

type Props = {
  children: ReactNode;
  marketConfig: CountrySelector;
  currentMarket: string;
  isItalianPage: boolean;
  cameoDomain: string;
  oetkerDomain: string;
};

type State = {
  currentMarket: string;
  isItalianPage: boolean;
  marketConfig: CountrySelector;
  redirectToMarket: (market: string) => void;
  currentSelectedMarketConfig?: CountrySelectorList;
};

const MarketContext = createContext<State | undefined>(undefined);

const findMarketConfig = (market: string, list: CountrySelectorList[]) => {
  return list.find((c) => c.countryCode.toLowerCase() === market.toLowerCase());
};

const MarketProvider = ({
  children,
  marketConfig,
  currentMarket,
  isItalianPage,
  cameoDomain,
  oetkerDomain,
}: Props) => {
  const router = useRouter();

  const redirectToMarket = (market: string) => {
    const isLocalhost = typeof window !== 'undefined' && window.location.hostname === 'localhost';

    // If the market is already the current market, do nothing
    if (market.toLowerCase() === currentMarket.toLowerCase()) {
      return;
    }

    // If we are on an Italian page, we need to switch to the selected market on oetker.com
    if (isItalianPage && market.toLowerCase() !== 'it' && !isLocalhost) {
      window.location.href = `https://${oetkerDomain}/${market}`;
      return;
    }

    // If we selected the Italian market, redirect to the cameo domain
    if (market === 'it' && !isLocalhost) {
      window.location.href = `https://${cameoDomain}`;
      return;
    }

    // Otherwise, redirect to the selected market's path
    router.push('/', undefined, { locale: market });
  };

  const currentSelectedMarketConfig = findMarketConfig(
    currentMarket,
    marketConfig.countrySelectorList || [],
  );

  return (
    <MarketContext.Provider
      value={{
        currentMarket,
        isItalianPage,
        marketConfig,
        redirectToMarket,
        currentSelectedMarketConfig,
      }}
    >
      {children}
    </MarketContext.Provider>
  );
};

const useMarket = () => {
  const context = useContext(MarketContext);
  if (!context) {
    throw new Error('useMarket must be used within a MarketProvider');
  }
  return context;
};

export { MarketProvider, useMarket };
